import React, {useEffect} from 'react';

const systemIdTag = 'responsive_style_event__';
const ResponsiveContext = React.createContext();

const styleParser = (device, styleInput) => {
  const guide = {
    tablet: ['tablet'],
    desktop: ['tablet', 'desktop'],
    xlScreen: ['tablet', 'desktop', 'xlScreen']
  }
  const baseStyles = styleInput[0];
  const dynamicStyles = styleInput[1];
  const isMultiRuled = dynamicStyles.tablet || dynamicStyles.desktop || dynamicStyles.xlScreen;
  let styles = {...baseStyles};
  const nonMobileTags = guide[device];
  if (device === 'mobile') { return styles; }
  if (isMultiRuled) {
    nonMobileTags.forEach(tag => styles = {...styles, ...dynamicStyles[tag]});
    return styles;
  }
  return {...baseStyles, ...dynamicStyles}
}

const calcScreenSize = width => {

  const size = width < 768 ? 'mobile'
    : document.body.clientWidth < 1024 ? 'tablet'
      : document.body.clientWidth < 3000 ? 'desktop'
        : 'xlScreen';
  const columns = [
    ['oneHalf', 100, 50],
    ['oneThird', 100, 33.3333],
    ['oneThirdFlex', 50, 33.3333],
    ['twoThirds', 100, 66.6666],
    ['oneFourth', 100, 25],
    ['oneFourthFlex', 50, 25],
    ['threeFourths', 100, 75],
    ['oneFifth', 100, 20],
    ['oneFifthFlex', 33.3333, 20],
    ['twoFifths', 100, 40],
    ['threeFifths', 100, 60],
    ['fourFifths', 100, 80]
  ]
  let retCols = {};
  columns.forEach((col) => {
    const width = size === 'mobile' ? col[1] : col[2];
    retCols[col[0]] = {
      width: `${width}%`,
      float: 'left',
      padding: width === 100 ? 0 : '0 10px',
    }
  });

  return {
    size,
    columns: retCols
  };
}


const reducer = (state, action) => {
  switch(action.type) {
    case 'add_resize_event':
    return {
      ...state,
      resizeEvents: {
        ...state.resizeEvents,
        [action.eventID]: action.event,
      }
    }
    case 'remove_resize_event':
    let resizeEvents = {...state.resizeEvents};
    delete resizeEvents[action.eventID];
    return {
      ...state,
      resizeEvents
    }
    case 'add_responsive_style':
      let newEvents = {...state.styleEvents};
      let newStyles = {...state.responsiveStyles}
      if (typeof action.id === 'object') {
        Object.keys(action.id).forEach(id => {
          const systemID = systemIdTag + id;
          const itemStyles = action.id[id];
          newEvents[systemID] = (size) => styleParser(size, itemStyles);
          newStyles[id] = styleParser(state.size, itemStyles);
        })
      } else {
        const systemID = systemIdTag + action.id;
        newEvents[systemID] = (size) => styleParser(size, action.styles);
        newStyles[action.id] = styleParser(state.size, action.styles);
      }
      return {
        ...state,
        styleEvents: newEvents,
        responsiveStyles: newStyles,
      };
    case 'remove_responsive_style':
      const styleEvents = { ...state.styleEvents };
      if (Array.isArray(action.id)) {
        action.id.forEach(id => delete styleEvents[systemIdTag + action.id])
      } else { delete styleEvents[systemIdTag + action.id]; }
      return {
        ...state,
        styleEvents,
      };
    default:
      const calculation = calcScreenSize(action.width);
      let styleUpdates = {...state.responsiveStyles};
      // resize event handlers
      const resizeHandlers = Object.keys(state.resizeEvents);
      const styleHandlers = Object.keys(state.styleEvents);
      if (resizeHandlers && resizeHandlers.length) resizeHandlers.forEach((event) => state.resizeEvents[event]());
      // responsive styles update on device change
      if (styleHandlers && state.size !== calculation.size) {
        styleHandlers.forEach((styleId) => {
          styleUpdates[styleId.replace(systemIdTag, '')] = state.styleEvents[styleId](calculation.size);
        })
      }
      return {
        ...state,
        size: calculation.size,
        columns: calculation.columns,
        responsiveStyles: styleUpdates,
      };
  }
}

const ResponsiveContextProvider = props => {

  // state
  const [state, dispatch] = React.useReducer(reducer, {
    size: calcScreenSize(document.body.clientWidth).size,
    columns: calcScreenSize(document.body.clientWidth).columns,
    resizeEvents: {},
    styleEvents: {},
    responsiveStyles: {},
  });

  // resize dispatch
  const resizeFunc = () => dispatch({width: document.body.clientWidth});

  // mount/unmount resize listener
  useEffect(() => {
    window.addEventListener('resize', resizeFunc)
    return(() => window.removeEventListener('resize', resizeFunc))
  }, [])

  const addResizeEvent = React.useCallback((id, event) => dispatch({
    type: 'add_resize_event',
    event,
    eventID: id
  }), [])
  const removeResizeEvent = React.useCallback((id) => dispatch({
    type: 'remove_resize_event',
    eventID: id
  }), [])

  // responsive style event mount/unmount
  const addStyle = React.useCallback((id, styleInput) => {
    dispatch({type: 'add_responsive_style', id: id, styles: styleInput});
  }, [])
  const removeStyle = React.useCallback(id => {
    dispatch({type: 'remove_responsive_style', id: id})
  }, [])

  return(
    <ResponsiveContext.Provider
      value={{
        device: state.size,
        columns: state.columns,
        addResizeEvent,
        removeResizeEvent,
        addStyle,
        removeStyle,
        rspStyles: state.responsiveStyles,
      }}
    >
      {props.children}
    </ResponsiveContext.Provider>
  )
}

const ResponsiveContextConsumer = ResponsiveContext.Consumer;
export {ResponsiveContext, ResponsiveContextProvider, ResponsiveContextConsumer};
